import { Vector3 } from '@babylonjs/core';
import { NPCController, NPCAIBehavior } from '../systems/NPCController';
import { logger } from '../core/Logger';

/**
 * Simple AI that patrols back and forth on the spline
 */
export class PatrolAI implements NPCAIBehavior {
  private direction: number = 1; // 1 = forward, -1 = backward
  private waitTimer: number = 0;
  private isWaiting: boolean = false;
  private waitDuration: number = 2.0; // seconds to wait at each end

  constructor(
    private minT: number = 0.2,
    private maxT: number = 0.8
  ) {}

  update(deltaTime: number, npc: NPCController): void {
    // Set camera vectors to point along the spline (for state machine logic)
    const splineForward = new Vector3(0, 0, 1);
    const splineRight = new Vector3(1, 0, 0);
    npc.setCameraVectors(splineForward, splineRight);

    // Check if at boundary
    if (npc.playerT >= this.maxT && this.direction > 0) {
      // Reached max, start waiting then turn around
      if (!this.isWaiting) {
        this.isWaiting = true;
        this.waitTimer = 0;
        npc.setMovementInput(0);
      }
    } else if (npc.playerT <= this.minT && this.direction < 0) {
      // Reached min, start waiting then turn around
      if (!this.isWaiting) {
        this.isWaiting = true;
        this.waitTimer = 0;
        npc.setMovementInput(0);
      }
    }

    // Handle waiting
    if (this.isWaiting) {
      this.waitTimer += deltaTime;
      if (this.waitTimer >= this.waitDuration) {
        // Finished waiting, turn around
        this.direction *= -1;
        this.isWaiting = false;
        npc.setMovementInput(this.direction);
      }
    } else {
      // Continue moving
      npc.setMovementInput(this.direction);
    }

    // Never jump or attack (for now)
    npc.setJumpInput(false);
    npc.setAttackInput(false);
  }
}

/**
 * Idle AI that doesn't move
 */
export class IdleAI implements NPCAIBehavior {
  update(_deltaTime: number, npc: NPCController): void {
    npc.setMovementInput(0);
    npc.setJumpInput(false);
    npc.setAttackInput(false);
    
    // Set neutral camera vectors
    const forward = new Vector3(0, 0, 1);
    const right = new Vector3(1, 0, 0);
    npc.setCameraVectors(forward, right);
  }
}

/**
 * AI that follows the player with rubber banding (stays within max distance)
 */
export class FollowAI implements NPCAIBehavior {
  private maxDistanceMeters: number;
  private minDistanceMeters: number;
  private isChasing: boolean = false;
  private debugLogCount: number = 0;
  private maxDebugLogs: number = 0; // Set to >0 for debugging, 0 to disable
  
  constructor(
    private getTargetT: () => number,
    maxDistanceMeters: number = 4.0, // Maximum distance in meters
    private splineLength: number = 1.0, // Total spline length for conversion
    minDistanceMeters: number = 2.0 // Minimum distance to maintain
  ) {
    this.maxDistanceMeters = maxDistanceMeters;
    this.minDistanceMeters = minDistanceMeters;
  }

  update(_deltaTime: number, npc: NPCController): void {
    // Set camera vectors to align with spline tangent for proper direction detection
    const tangent = npc.getSplineTangent();
    npc.setCameraVectors(tangent, tangent);
    
    // Get distance to player along the spline
    const playerT = this.getTargetT();
    const npcT = npc.playerT;
    const deltaT = playerT - npcT;
    
    // Convert T distance to meters
    const distanceMeters = Math.abs(deltaT) * this.splineLength;
    
    // Use hysteresis to prevent constant toggling
    // Start chasing if too far, stop chasing if close enough
    if (!this.isChasing && distanceMeters > this.maxDistanceMeters) {
      this.isChasing = true;
    } else if (this.isChasing && distanceMeters < this.minDistanceMeters) {
      this.isChasing = false;
    }
    
    // Determine direction to player along the spline
    // Positive deltaT means player is ahead (higher T), so direction is +1
    // Negative deltaT means player is behind (lower T), so direction is -1
    const directionToPlayer = deltaT !== 0 ? Math.sign(deltaT) : 1;
    
    // Apply movement based on chasing state
    const moveInput = this.isChasing ? directionToPlayer : 0;
    npc.setMovementInput(moveInput);
    
    // Debug logging (first few frames)
    if (this.debugLogCount < this.maxDebugLogs) {
      logger.npc.debug(`🤖 FollowAI #${this.debugLogCount + 1}: dist=${distanceMeters.toFixed(2)}m, chase=${this.isChasing}, move=${moveInput}`);
      this.debugLogCount++;
    }
    
    // ALWAYS face TOWARD the player along the spline!
    // This is the direction the NPC would need to walk to reach the player.
    npc.setFacingDirection(directionToPlayer);
    
    npc.setJumpInput(false);
    npc.setAttackInput(false);
  }
}

/**
 * AI that runs in a circle (moves forward continuously)
 */
export class RunInCircleAI implements NPCAIBehavior {
  update(_deltaTime: number, npc: NPCController): void {
    // Set camera vectors to align with spline tangent
    const tangent = npc.getSplineTangent();
    const right = tangent;
    const forward = Vector3.Cross(Vector3.Up(), right).normalize();
    npc.setCameraVectors(forward, right);
    
    // Move forward continuously
    npc.setMovementInput(1);
    npc.setJumpInput(false);
    npc.setAttackInput(false);
  }
}

// Re-export for convenience
export { SplinePatrolAI } from './SplinePatrolAI';
export type { SplinePatrolConfig } from './SplinePatrolAI';
